import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

/**
 * One-tap "How was today?" mood check. Shown after a worker submits their
 * timesheet so we capture vibe while it's fresh.
 *
 * Three big emoji buttons — green 'good', yellow 'average', red 'rough'.
 * Optional one-line note for context. Workers can skip — never gates the
 * timesheet submission.
 */
export function MoodPrompt({ open, onClose, timesheetId, date }) {
  const [allowedFor, setAllowedFor] = useState(null);
  useEffect(() => {
    let current = true;
    setAllowedFor(null);
    if (open) {
      setMood(null); setNote("");
      api.get("/org/engagement-settings", { __flSkipReadCache: true }).then(({data}) => {
        if (current && data.mood_tracking_enabled) setAllowedFor(timesheetId);
      }).catch(() => {});
    }
    return () => { current = false; };
  }, [open, timesheetId]);
  const [mood, setMood] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (chosen) => {
    const m = chosen || mood;
    if (!m) return;
    setBusy(true);
    try {
      await api.post("/moods", {
        mood: m,
        date: date || undefined,
        note: note.trim() || undefined,
        timesheet_id: timesheetId || undefined,
      });
      toast.success("Cheers — noted.");
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open && allowedFor !== null && allowedFor === timesheetId} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="rounded-sm max-w-md" data-testid="mood-prompt-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight text-2xl">How was your day?</DialogTitle>
          <DialogDescription>
            One tap. Office doesn&apos;t see individual answers in real-time — just a heat-map over weeks, so they can spot if the crew is grinding.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-3 gap-2">
          <MoodButton emoji="🟢" label="Good" sub="Solid day" v="good" current={mood} onPick={(v) => { setMood(v); submit(v); }} busy={busy} />
          <MoodButton emoji="🟡" label="Average" sub="Just a day" v="average" current={mood} onPick={(v) => { setMood(v); submit(v); }} busy={busy} />
          <MoodButton emoji="🔴" label="Rough" sub="Tough one" v="rough" current={mood} onPick={(v) => { setMood(v); submit(v); }} busy={busy} />
        </div>

        <div className="pt-2">
          <Input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Anything to flag? (optional)"
            className="rounded-sm border-zinc-400 text-sm"
            maxLength={200}
            data-testid="mood-prompt-note"
          />
        </div>

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onClose} disabled={busy} className="rounded-sm text-xs uppercase tracking-wider font-bold text-zinc-500" data-testid="mood-prompt-skip">
            Skip
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MoodButton({ emoji, label, sub, v, current, onPick, busy }) {
  const active = current === v;
  return (
    <button
      type="button"
      onClick={() => onPick(v)}
      disabled={busy}
      className={`flex flex-col items-center gap-1 p-3 border-2 rounded-sm transition-all ${
        active
          ? "border-[#FF5F15] bg-orange-50 shadow-[3px_3px_0_0_#FF5F15]"
          : "border-zinc-300 hover:border-zinc-500 hover:-translate-y-0.5"
      } disabled:opacity-50`}
      data-testid={`mood-prompt-${v}`}
    >
      <span className="text-3xl" aria-hidden="true">{emoji}</span>
      <span className="font-bold uppercase tracking-tight text-xs">{label}</span>
      <span className="text-[10px] font-mono text-zinc-500">{sub}</span>
    </button>
  );
}
